import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";

import { LoanInterestRateUpdateOperationEnum, MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import theme from "@/theme";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { useDeleteMonthlyCashflowResult } from "../../../hooks/useDeleteMonthlyCashflowResult";
import { useSaveLoanInterestRate } from "../../../hooks/useSaveLoanInterestRate";
import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

import { LoanInfoHistoryEditor } from "./HistoryEditor";
import { InterestRateEditor } from "./InterestRateEditor";
import { LoanConditionsRepair } from "./LoanConditionsRepair";
import { LoanType } from "./types";

interface props {
  loanType: LoanType;
  monthlyCashflowResult: MonthlyCashflowResult;
}

export const LoanInfoHistoryItem: React.FC<props> = ({ monthlyCashflowResult, loanType }) => {
  const [isShowEdior, setIsShowEditor] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const { save, loading, error, errorReason, clearError } = useSaveLoanInterestRate();
  const selected_property = useRecoilValue(SelectedProperty);
  const { deleteMonthlyCashflowResult } = useDeleteMonthlyCashflowResult();

  // 編集ボタンクリック時の処理
  const onClickEdit = React.useCallback(() => {
    setIsShowEditor(true);
  }, []);

  // 削除ボタンクリック時の処理
  const onClickDelete = React.useCallback(async () => {
    const fieldsToDelete = ["loan_prepayment", "loan_prepayment_method"];
    const deleteFields = {};
    fieldsToDelete.forEach((field) => {
      deleteFields[field] = null;
    });

    await deleteMonthlyCashflowResult(monthlyCashflowResult.month, deleteFields);
  }, [loanType, monthlyCashflowResult.month, deleteMonthlyCashflowResult]);

  // 返済方式map
  const loanPrepaymentMethodMap = {
    ReduceLoanAmount: "返済額軽減型",
    ReduceLoanPaymentPeriod: "返済期間短縮型",
  };

  const getHistoryContents = (loanType: LoanType, data: MonthlyCashflowResult) => {
    const startMonth = dayjs(data.month).format("YYYY/MM");
    switch (loanType) {
      case "借入利率変動":
        if (data.loan_interest_rate_percentage == null) return [];
        return [
          { label: "金利の適用開始年月", value: startMonth },
          {
            label: "借入利率 (年利) ",
            value: `${data.loan_interest_rate_percentage}%`,
          },
        ];
      case "繰上返済":
        if (!data.loan_prepayment && !data.loan_prepayment_method) return [];
        return [
          { label: "支払開始月", value: startMonth },
          {
            label: "返済金額",
            value: data.loan_prepayment ? `${data.loan_prepayment.toLocaleString()}円` : "-円",
          },
          {
            label: "返済方式",
            value: data.loan_prepayment_method ? loanPrepaymentMethodMap[data.loan_prepayment_method] : "-",
          },
        ];
      default:
        return [];
    }
  };

  if (getHistoryContents(loanType, monthlyCashflowResult).length === 0) {
    return null;
  }

  return (
    <Box>
      {isShowEdior ? (
        loanType === "借入利率変動" ? (
          <InterestRateEditor
            history={monthlyCashflowResult}
            onCancel={() => setIsShowEditor(false)}
            onSaved={() => setIsShowEditor(false)}
          />
        ) : (
          <LoanInfoHistoryEditor
            monthlyCashflowResult={monthlyCashflowResult}
            onCancel={() => setIsShowEditor(false)}
            onSave={() => setIsShowEditor(false)}
            propertyBoughtAt={selected_property?.property.bought_at}
          />
        )
      ) : (
        <EditPropertyHistoryItem
          contents={getHistoryContents(loanType, monthlyCashflowResult)}
          onEdit={onClickEdit}
          editLabel={loanType === "借入利率変動" ? "訂正" : "編集"}
          onDelete={
            loanType === "借入利率変動"
              ? () => {
                  clearError();
                  setConfirmDelete(true);
                }
              : onClickDelete
          }
        />
      )}
      {isShowEdior && (
        <CustomButton
          type="button"
          tone="text"
          onClick={
            loanType === "借入利率変動"
              ? () => {
                  clearError();
                  setConfirmDelete(true);
                }
              : onClickDelete
          }
          style={{ color: theme.palette.semantic["status/error-text"], marginTop: 16 }}
        >
          この記録を削除する
        </CustomButton>
      )}

      <Dialog
        PaperProps={{ style: { maxWidth: 480, borderRadius: 16 } }}
        open={confirmDelete}
        onClose={() => {
          if (!loading) {
            clearError();
            setConfirmDelete(false);
          }
        }}
      >
        <DialogTitle>金利履歴を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            誤登録の取り消しに使用します。削除した月以降は直前の金利で再計算されます。初回返済月の金利が無くなる場合は削除できません。
          </Typography>
          <LoanConditionsRepair reason={errorReason} onRepaired={clearError} />
          {error && (
            <Typography role="alert" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            customVariant="textPrimary"
            disabled={loading}
            onClick={() => {
              clearError();
              setConfirmDelete(false);
            }}
          >
            キャンセル
          </CustomButton>
          <CustomButton
            customVariant="outlinedGray"
            disabled={loading}
            onClick={async () => {
              if (
                await save({
                  operation: LoanInterestRateUpdateOperationEnum.Delete,
                  month: monthlyCashflowResult.month,
                })
              )
                setConfirmDelete(false);
            }}
          >
            削除する
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
